import {
  COLLECTIONS,
  JOB_ACCESS_SECRET_DOC,
  type ActivityLogDoc,
  type JobAccessSecretDoc,
  type JobDoc,
  type RegistrationDoc,
} from '@crew/shared';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type FirestoreError,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { demoStore } from '@/demo/demoStore';
import { IS_DEMO } from '@/demo/demoMode';
import type { Ts } from '@/lib/ts';
import { getFirebase } from '@/services/firebase';

/**
 * נתוני העבודות בזמן אמת. מנהלים קוראים ישירות מ-Firestore (קריאה בלבד);
 * כל המנהלים רואים את אותם נתונים ואת אותם עדכונים מיד.
 * במצב תצוגה הנתונים מגיעים ממאגר מקומי בזיכרון.
 */

export type JobView = JobDoc<Ts> & { id: string };
export type RegistrationView = RegistrationDoc<Ts> & { id: string };
export type ActivityView = ActivityLogDoc<Ts> & { id: string };
export type JobSecretView = JobAccessSecretDoc<Ts>;

export type LiveQuery<T> = { state: 'loading' } | { state: 'ready'; data: T } | { state: 'error'; message: string };

export function firestoreErrorMessage(code: string): string {
  switch (code) {
    case 'permission-denied':
      return 'אין הרשאת קריאה לנתונים. יש לוודא שכללי Firestore פורסו ושקיים מסמך מנהל פעיל עבור החשבון.';
    case 'failed-precondition':
      return 'חסר אינדקס ב-Firestore. יש לפרוס את firestore.indexes.json (ראו README).';
    case 'unavailable':
      return 'אין חיבור לשרת הנתונים. מוצגים הנתונים האחרונים שנשמרו במכשיר, אם יש.';
    default:
      return 'טעינת הנתונים נכשלה.';
  }
}

/** תשתית משותפת: הרשמה לשינויים במקור (Firestore או מאגר התצוגה). */
function useLive<T>(
  key: string,
  demo: () => T,
  firebase: (onData: (data: T) => void, onError: (e: FirestoreError) => void) => () => void,
): LiveQuery<T> {
  const [result, setResult] = useState<LiveQuery<T>>({ state: 'loading' });
  useEffect(() => {
    setResult({ state: 'loading' });
    if (IS_DEMO) {
      const push = () => setResult({ state: 'ready', data: demo() });
      push();
      return demoStore.subscribe(push);
    }
    return firebase(
      (data) => setResult({ state: 'ready', data }),
      (err) => setResult({ state: 'error', message: firestoreErrorMessage(err.code) }),
    );
    // key מייצג את כל התלויות של השאילתה
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return result;
}

const withId = <T,>(d: { id: string; data(): DocumentData }) => ({ id: d.id, ...(d.data() as T) });

export function useActiveJobs(max = 200): LiveQuery<JobView[]> {
  return useLive(
    `active:${max}`,
    () => demoStore.jobs('active'),
    (onData, onError) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.jobs), where('status', '==', 'active'), orderBy('date', 'asc'), limit(max));
      return onSnapshot(q, (snap) => onData(snap.docs.map((d) => withId<JobDoc<Ts>>(d))), onError);
    },
  );
}

export function useArchivedJobs(max = 100): LiveQuery<JobView[]> {
  return useLive(
    `archived:${max}`,
    () => demoStore.jobs('archived'),
    (onData, onError) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.jobs), where('status', '==', 'archived'), orderBy('date', 'desc'), limit(max));
      return onSnapshot(q, (snap) => onData(snap.docs.map((d) => withId<JobDoc<Ts>>(d))), onError);
    },
  );
}

/** עבודה אחת. data = null אם העבודה לא קיימת (או נמחקה). */
export function useJob(jobId: string): LiveQuery<JobView | null> {
  return useLive(
    `job:${jobId}`,
    () => demoStore.job(jobId),
    (onData, onError) => {
      const { db } = getFirebase();
      return onSnapshot(
        doc(db, COLLECTIONS.jobs, jobId),
        (snap) => onData(snap.exists() ? { id: snap.id, ...(snap.data() as JobDoc<Ts>) } : null),
        onError,
      );
    },
  );
}

export function useJobSecret(jobId: string): LiveQuery<JobSecretView | null> {
  return useLive(
    `secret:${jobId}`,
    () => demoStore.secret(jobId),
    (onData, onError) => {
      const { db } = getFirebase();
      return onSnapshot(
        doc(db, COLLECTIONS.jobs, jobId, COLLECTIONS.jobSecrets, JOB_ACCESS_SECRET_DOC),
        (snap) => onData(snap.exists() ? (snap.data() as JobSecretView) : null),
        onError,
      );
    },
  );
}

export function useJobRegistrations(jobId: string): LiveQuery<RegistrationView[]> {
  return useLive(
    `registrations:${jobId}`,
    () => demoStore.registrations(jobId),
    (onData, onError) => {
      const { db } = getFirebase();
      return onSnapshot(
        collection(db, COLLECTIONS.jobs, jobId, COLLECTIONS.registrations),
        (snap) => onData(snap.docs.map((d) => withId<RegistrationDoc<Ts>>(d))),
        onError,
      );
    },
  );
}

export function useJobActivity(jobId: string, max = 50): LiveQuery<ActivityView[]> {
  return useLive(
    `activity:${jobId}:${max}`,
    () => demoStore.activity(jobId),
    (onData, onError) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.activityLog), where('jobId', '==', jobId), orderBy('createdAt', 'desc'), limit(max));
      return onSnapshot(q, (snap) => onData(snap.docs.map((d) => withId<ActivityLogDoc<Ts>>(d))), onError);
    },
  );
}
